import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Check, AlertTriangle, Loader2, ShieldCheck, CalendarDays, ArrowRight,
  CreditCard, Star, XCircle, FileText, Clock,
} from "lucide-react";
import Seo from "../components/Seo";
import { LogoLockup } from "../components/LogoMark";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fieldCls = "w-full bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-steel-light/60 transition-all";
const locOf = (lang) => (lang === "fr" ? "fr-CA" : "en-CA");
const fmtMoney = (n, lang) => lang === "fr"
  ? `${Number(n).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $ CA`
  : `CA$${Number(n).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDay = (dateStr, lang, opts = { weekday: "long", month: "long", day: "numeric" }) =>
  new Date(dateStr + "T12:00:00").toLocaleDateString(locOf(lang), opts);

const STEPS = ["Quote Submitted", "Quote Reviewed", "Quote Accepted", "Payment", "Scheduled", "Completed", "Review Requested"];
const STEP_COUNT = {
  new: 2, manual_review: 1, under_review: 1, quote_sent: 2,
  accepted_awaiting_payment: 3, payment_expired: 3, paid_scheduling_required: 4,
  scheduled: 5, in_progress: 5, completed: 7, cancelled: 0,
};

function Stepper({ status, t }) {
  const done = STEP_COUNT[status] ?? 1;
  return (
    <div data-testid="proposal-stepper" className="glass rounded-3xl p-5 md:p-6 overflow-x-auto">
      <div className="flex items-center min-w-[640px] md:min-w-0">
        {STEPS.map((s, i) => {
          const isDone = i < done;
          const isActive = i === done;
          return (
            <React.Fragment key={s}>
              <div className="flex flex-col items-center gap-2 flex-1">
                <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-600 shrink-0 ${isDone ? "bg-steel-light text-ink-950" : isActive ? "border-2 border-steel-light text-steel-light" : "border border-white/15 text-white/30"}`}>
                  {isDone ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : i + 1}
                </span>
                <span className={`text-[10px] md:text-[11px] text-center leading-tight ${isDone || isActive ? "text-white/80" : "text-white/30"}`}>{t(s)}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`h-px flex-1 mx-1 mb-5 ${i < done - 1 ? "bg-steel-light/60" : "bg-white/10"}`} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function AcceptDialog({ p, token, onClose, onAccepted, t }) {
  const { lang } = useLang();
  const [f, setF] = useState({ name: p.full_name || "", email: p.email || "", phone: p.phone || "", service_address: "", a1: false, a2: false, a3: false });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const boxes = [
    ["a1", t("I agree to the quoted scope of work.")],
    ["a2", t("I agree to North Peak Facility Services' terms and cancellation policy.")],
    ["a3", t("I understand the quoted price and applicable taxes.")],
  ];
  const submit = async () => {
    if (!f.name.trim() || !f.email.trim() || !f.phone.trim() || !f.service_address.trim()) { toast.error(t("Please fill in all fields, including the full service address.")); return; }
    if (!f.a1 || !f.a2 || !f.a3) { toast.error(t("Please confirm all three checkboxes to accept the quote.")); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/proposal/${token}/accept`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: f.name, email: f.email, phone: f.phone, service_address: f.service_address, agree_scope: f.a1, agree_terms: f.a2, agree_price: f.a3 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "failed");
      toast.success(t("Quote accepted! Check your email for confirmation."));
      onAccepted();
    } catch (e) {
      toast.error(typeof e.message === "string" && e.message !== "failed" ? e.message : t("Something went wrong. Please try again."));
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.96, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()} data-testid="accept-dialog"
        className="glass-strong rounded-3xl p-6 md:p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 className="font-serif text-2xl text-white mb-1" style={{ fontWeight: 500 }}>{t("Confirm Your Acceptance")}</h3>
        <p className="text-white/50 text-sm mb-6">{t("Quote")} {p.reference_id} · {fmtMoney(p.pricing.total, lang)} {t("taxes included")}</p>
        <div className="space-y-3 mb-5">
          <input data-testid="accept-name" className={fieldCls} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder={t("Full Name")} />
          <input data-testid="accept-email" type="email" className={fieldCls} value={f.email} onChange={(e) => set("email", e.target.value)} placeholder={t("Email")} />
          <input data-testid="accept-phone" type="tel" className={fieldCls} value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder={t("Phone")} />
          <input data-testid="accept-address" className={fieldCls} value={f.service_address} onChange={(e) => set("service_address", e.target.value)} placeholder={t("Full service address (street, city, postal code)")} />
        </div>
        <div className="space-y-3 mb-6">
          {boxes.map(([k, label]) => (
            <label key={k} className="flex items-start gap-3 cursor-pointer">
              <button type="button" data-testid={`accept-check-${k}`} onClick={() => set(k, !f[k])}
                className={`h-5 w-5 mt-0.5 rounded-md flex items-center justify-center shrink-0 border transition-all ${f[k] ? "bg-steel-light border-steel-light text-ink-950" : "border-white/25"}`}>
                {f[k] && <Check className="h-3 w-3" strokeWidth={3} />}
              </button>
              <span className="text-white/70 text-sm leading-relaxed">{label}</span>
            </label>
          ))}
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="btn-pill glass text-white/70 px-6 py-3 text-sm flex-1">{t("Cancel")}</button>
          <button data-testid="accept-confirm" onClick={submit} disabled={busy}
            className="btn-pill bg-white text-ink-950 px-6 py-3 text-sm font-600 flex-1 flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} {t("Accept Quote")}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function Scheduler({ token, onScheduled, t, mode = "schedule" }) {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    fetch(`${API}/proposal/${token}/slots`).then((r) => r.json()).then(setData).catch(() => setData({ days: [] }));
  }, [token]);
  const book = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      const endpoint = mode === "hold" ? "hold-slot" : "schedule";
      const res = await fetch(`${API}/proposal/${token}/${endpoint}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: sel.date, start: sel.start }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "failed");
      toast.success(mode === "hold" ? t("Time reserved! It's held for 30 minutes while you complete your deposit.") : t("Appointment confirmed!"));
      onScheduled();
    } catch (e) {
      toast.error(typeof e.message === "string" && e.message !== "failed" ? e.message : t("Something went wrong. Please try again."));
    } finally { setBusy(false); }
  };
  if (!data) return <div className="text-white/40 text-sm py-6 text-center">{t("Loading available times…")}</div>;
  return (
    <div data-testid="scheduler">
      <p className="text-white/60 text-sm mb-4">{t("Choose a 2-hour arrival window. Estimated duration:")} ~{Math.round((data.duration_min || 120) / 60)}h</p>
      <div className="space-y-4 max-h-80 overflow-y-auto pr-1">
        {data.days.map((day) => (
          <div key={day.date}>
            <p className="text-white/50 text-xs uppercase tracking-wide mb-2">{fmtDay(day.date, lang)}</p>
            <div className="flex flex-wrap gap-2">
              {day.slots.map((s) => {
                const on = sel && sel.date === day.date && sel.start === s;
                return (
                  <button key={s} data-testid={`slot-${day.date}-${s}`} onClick={() => setSel({ date: day.date, start: s })}
                    className={`px-4 py-2.5 rounded-full border text-sm transition-all ${on ? "bg-white text-ink-950 border-white font-600" : "border-white/15 text-white/70 hover:border-white/40"}`}>
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {data.days.length === 0 && <p className="text-white/40 text-sm">{t("No available times right now — we'll contact you to schedule.")}</p>}
      </div>
      <button data-testid="confirm-appointment" onClick={book} disabled={!sel || busy}
        className="btn-pill mt-6 w-full bg-white text-ink-950 py-3.5 text-sm font-600 flex items-center justify-center gap-2 disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />} {mode === "hold" ? t("Reserve This Time") : t("Confirm Appointment")}
      </button>
    </div>
  );
}

export default function Proposal() {
  const { token } = useParams();
  const { t, lang } = useLang();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [showAccept, setShowAccept] = useState(false);
  const [reqBusy, setReqBusy] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/proposal/${token}`).then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setP).catch(() => setErr(true));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  // Verify Stripe payment on return from checkout (?session_id=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (params.get("payment") === "cancelled") {
      toast.info(t("Payment cancelled — your quote is still waiting for you."));
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (!sessionId) return;
    window.history.replaceState({}, "", window.location.pathname);
    setVerifying(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await fetch(`${API}/payments/status/${sessionId}`);
        const d = await r.json();
        if (d.payment_status === "paid") {
          toast.success(t("Deposit received — your booking is confirmed!"));
          setVerifying(false);
          load();
          return;
        }
        if (d.payment_status === "expired") {
          toast.error(t("Payment session expired. Please try again."));
          setVerifying(false);
          return;
        }
      } catch { /* keep polling */ }
      if (attempts < 8) setTimeout(poll, 2000);
      else { setVerifying(false); toast.info(t("We're still confirming your payment — refresh this page in a moment.")); }
    };
    poll();
  }, []); // eslint-disable-line

  const payDeposit = async () => {
    setPayBusy(true);
    try {
      const res = await fetch(`${API}/proposal/${token}/deposit-checkout`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "failed");
      window.location.href = d.checkout_url;
    } catch (e) {
      toast.error(typeof e.message === "string" && e.message !== "failed" ? e.message : t("Something went wrong. Please try again."));
      setPayBusy(false);
    }
  };

  const requestFinal = async () => {
    setReqBusy(true);
    try {
      const res = await fetch(`${API}/proposal/${token}/request-final-quote`, { method: "POST" });
      if (!res.ok) throw new Error();
      toast.success(t("Request sent! We'll confirm your final quote shortly."));
      load();
    } catch { toast.error(t("Something went wrong. Please try again.")); }
    finally { setReqBusy(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-ink-950 flex items-center justify-center px-6">
      <div className="glass rounded-3xl p-10 text-center max-w-md">
        <XCircle className="h-10 w-10 text-white/30 mx-auto mb-4" />
        <p className="text-white/70">{t("This quote link is invalid or no longer available.")}</p>
        <Link to="/quote" className="btn-pill inline-flex mt-6 bg-white text-ink-950 px-6 py-3 text-sm font-600">{t("Get a New Quote")}</Link>
      </div>
    </div>
  );
  if (!p) return <div className="min-h-screen bg-ink-950 flex items-center justify-center text-white/40">{t("Loading…")}</div>;

  const sizeLabel = p.property_type === "Office / Commercial Space" || p.sqft
    ? (p.sqft ? `${Number(p.sqft).toLocaleString()} ${t("sq ft")}` : null)
    : p.quantity && !p.bedrooms ? `${p.quantity}` : null;
  const resSize = p.bedrooms ? `${p.bedrooms === "Studio" ? t("Studio") : p.bedrooms} ${t("bed")} · ${p.bathrooms} ${t("bath")}` : null;
  const canAccept = p.pricing && ["new", "quote_sent", "under_review", "manual_review"].includes(p.status) && !p.expired;
  const showManual = !p.pricing;
  const details = [
    [t("Quote Number"), p.reference_id],
    [t("Quote Date"), new Date(p.created_at).toLocaleDateString(locOf(lang))],
    [t("Valid Until"), p.expires_at ? new Date(p.expires_at).toLocaleDateString(locOf(lang)) : "—"],
    [t("Customer"), p.full_name], [t("Email"), p.email], [t("Phone"), p.phone],
    [t("Service Address"), p.service_address || `${t("Postal code")}: ${p.postal_code}`],
    [t("Service"), t(p.service)], [t("Property Type"), t(p.property_type)],
    [t("Size"), resSize || sizeLabel || (p.quantity ? String(p.quantity) : "—")],
    [t("Frequency"), t(p.frequency)], [t("Condition"), t(p.condition)],
    [t("Add-ons"), p.addons.length ? p.addons.map((a) => t(a)).join(", ") : t("None")],
  ];

  return (
    <div data-testid="proposal-page" className="min-h-screen bg-ink-950 pb-20">
      <Seo title={`Quote ${p.reference_id}`} path={`/proposal/${token}`} description="Your North Peak Facility Services quote and booking status." />
      <header className="border-b border-white/8 px-6 md:px-12 py-5 flex items-center justify-between">
        <Link to="/"><LogoLockup dark className="h-8" /></Link>
        <span className="glass rounded-full px-4 py-1.5 text-xs text-white/70">{t(p.status_label)}</span>
      </header>

      <div className="max-w-5xl mx-auto px-5 md:px-8 pt-8 space-y-6">
        <div>
          <p className="overline text-steel-light mb-1">{t("Your Quote & Booking Status")}</p>
          <h1 className="font-serif text-gradient text-3xl md:text-4xl" style={{ fontWeight: 500 }}>{t("Quote")} {p.reference_id}</h1>
        </div>

        <Stepper status={p.status} t={t} />

        {p.status === "cancelled" && (
          <div className="glass rounded-3xl p-6 border border-red-400/20 flex items-center gap-3 text-red-300/90 text-sm" data-testid="cancelled-banner">
            <XCircle className="h-5 w-5 shrink-0" /> {t("This quote has been cancelled. Contact us if you'd like a new quote.")}
          </div>
        )}
        {p.expired && ["new", "quote_sent", "manual_review", "under_review"].includes(p.status) && (
          <div className="glass rounded-3xl p-6 border border-amber-400/20 flex items-center gap-3 text-amber-200/90 text-sm" data-testid="expired-banner">
            <Clock className="h-5 w-5 shrink-0" /> {t("This quote has expired. Contact us for an updated quote.")}
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-6 items-start">
          {/* Details + scope + terms */}
          <div className="lg:col-span-3 space-y-6 order-last lg:order-none">
            <div className="glass rounded-3xl p-6 md:p-8" data-testid="proposal-details">
              <p className="overline text-steel-light mb-4">{t("Quote Details")}</p>
              <div className="grid sm:grid-cols-2 gap-x-8">
                {details.map(([k, v]) => (
                  <div key={k} className="flex items-start justify-between gap-4 py-2.5 border-b border-white/6 text-sm">
                    <span className="text-white/45">{k}</span><span className="text-white/85 text-right">{v}</span>
                  </div>
                ))}
              </div>
              {p.details && <p className="mt-4 text-white/55 text-sm italic">"{p.details}"</p>}
            </div>

            <div className="glass rounded-3xl p-6 md:p-8" data-testid="proposal-scope">
              <p className="overline text-steel-light mb-4">{t("Scope of Work")}</p>
              <ul className="space-y-2.5">
                {p.scope_of_work.map((s) => (
                  <li key={s} className="flex items-center gap-3 text-sm text-white/75">
                    <span className="h-5 w-5 rounded-full bg-steel/20 flex items-center justify-center shrink-0"><Check className="h-3 w-3 text-steel-light" strokeWidth={2.5} /></span>
                    {t(s)}
                  </li>
                ))}
              </ul>
            </div>

            <div className="glass rounded-3xl p-6 md:p-8" data-testid="proposal-terms">
              <p className="overline text-steel-light mb-4">{t("Important Notes, Terms & Conditions")}</p>
              <ol className="space-y-3 list-decimal pl-4">
                {p.terms.map((s, i) => <li key={i} className="text-white/55 text-xs leading-relaxed">{t(s)}</li>)}
              </ol>
            </div>
          </div>

          {/* Pricing + action */}
          <div className="lg:col-span-2 lg:sticky lg:top-6 space-y-6 order-first lg:order-none">
            <div className="glass-strong rounded-3xl p-6 md:p-8" data-testid="proposal-pricing">
              {p.pricing ? (
                <>
                  <p className="overline text-steel-light mb-3">{t("Estimated starting price")}</p>
                  <div className="space-y-2 text-sm">
                    {[[t("Subtotal"), p.pricing.subtotal], [t("GST (5%)"), p.pricing.gst], [t("QST (9.975%)"), p.pricing.qst]].map(([k, v]) => (
                      <div key={k} className="flex justify-between text-white/60"><span>{k}</span><span className="tabular-nums">{fmtMoney(v, lang)}</span></div>
                    ))}
                    <div className="flex justify-between pt-3 border-t border-white/10 text-white font-600 text-base">
                      <span>{t("Total")}</span><span className="tabular-nums" data-testid="proposal-total">{fmtMoney(p.pricing.total, lang)}</span>
                    </div>
                  </div>
                  <p className="mt-4 text-white/40 text-xs leading-relaxed">{t("This is an estimated starting price — not a final invoice. Final pricing is confirmed by North Peak Facility Services after reviewing the job requirements.")}</p>
                </>
              ) : (
                <div data-testid="proposal-manual-block">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="h-5 w-5 text-amber-300" />
                    <p className="text-amber-200 font-600 text-sm tracking-wide">{t("MANUAL REVIEW REQUIRED")}</p>
                  </div>
                  <p className="text-white/60 text-sm leading-relaxed">{t("North Peak Facility Services will review your request and confirm final pricing before the job is scheduled.")}</p>
                </div>
              )}

              {/* Stage actions */}
              <div className="mt-6">
                {canAccept && p.pricing && (
                  <button data-testid="accept-quote-btn" onClick={() => setShowAccept(true)}
                    className="btn-pill w-full bg-white text-ink-950 py-4 text-sm font-600 flex items-center justify-center gap-2 hover:shadow-xl hover:shadow-white/20">
                    {t("Accept Quote")} <ArrowRight className="h-4 w-4" />
                  </button>
                )}
                {showManual && ["manual_review", "new"].includes(p.status) && (
                  <button data-testid="request-final-quote-btn" onClick={requestFinal} disabled={reqBusy}
                    className="btn-pill w-full bg-white text-ink-950 py-4 text-sm font-600 flex items-center justify-center gap-2 disabled:opacity-60">
                    {reqBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} {t("Request Final Quote")}
                  </button>
                )}
                {p.status === "under_review" && (
                  <p className="text-white/50 text-sm text-center" data-testid="under-review-note">{t("Our team is reviewing your request and will confirm your final quote shortly.")}</p>
                )}
              </div>
            </div>

            {(p.status === "accepted_awaiting_payment" || p.status === "payment_expired") && p.pricing && (
              <div className="glass-strong rounded-3xl p-6 md:p-8" data-testid="payment-stage">
                <p className="overline text-steel-light mb-3">{t("Secure Your Booking")}</p>
                <div className="flex items-end justify-between mb-2">
                  <span className="text-white/60 text-sm">{t("Deposit (25%)")}</span>
                  <span className="font-serif text-gradient text-3xl tabular-nums" data-testid="deposit-amount" style={{ fontWeight: 500 }}>{fmtMoney(p.pricing.deposit, lang)}</span>
                </div>
                <p className="text-white/45 text-xs leading-relaxed mb-5">{t("Your deposit is applied to the final invoice. The remaining balance is due upon completion.")}</p>

                {p.status === "payment_expired" && (
                  <div className="rounded-2xl border border-amber-400/30 bg-amber-400/10 px-4 py-3.5 mb-4 flex items-start gap-3" data-testid="payment-expired-notice">
                    <Clock className="h-4 w-4 text-amber-300 shrink-0 mt-0.5" />
                    <p className="text-amber-200/90 text-xs leading-relaxed">{t("Your payment window expired and the reserved time was released. Please reserve a new time below.")}</p>
                  </div>
                )}

                {p.held_appointment ? (
                  <div className="rounded-2xl border border-steel-light/30 bg-steel/10 px-4 py-4 mb-4" data-testid="held-slot-card">
                    <div className="flex items-center gap-3">
                      <CalendarDays className="h-4 w-4 text-steel-light shrink-0" />
                      <div>
                        <p className="text-white text-sm font-600">{fmtDay(p.held_appointment.date, lang)} · {p.held_appointment.start}</p>
                        <p className="text-white/50 text-xs mt-0.5">{t("Reserved for you until")} {new Date(p.held_appointment.held_until).toLocaleTimeString(locOf(lang), { hour: "2-digit", minute: "2-digit" })} — {t("confirmed once your deposit is received.")}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mb-4" data-testid="hold-slot-picker">
                    <p className="text-white/60 text-sm font-600 mb-3">{t("Reserve your preferred time")}</p>
                    <p className="text-white/45 text-xs mb-3">{t("Your time is held for 30 minutes while you complete the deposit. It's only confirmed after payment.")}</p>
                    <Scheduler token={token} onScheduled={load} t={t} mode="hold" />
                  </div>
                )}

                <div className="mb-4">
                  <button data-testid="pay-deposit-btn" onClick={payDeposit} disabled={payBusy || verifying}
                    className="btn-pill w-full bg-white text-ink-950 py-4 text-sm font-600 flex items-center justify-center gap-2 hover:shadow-xl hover:shadow-white/20 disabled:opacity-60">
                    {payBusy || verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                    {verifying ? t("Confirming your payment…") : `${t("Pay Deposit")} — ${fmtMoney(p.pricing.deposit, lang)}`}
                  </button>
                  <p className="mt-2.5 text-center text-white/40 text-xs flex items-center justify-center gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5" /> {t("Secure checkout powered by Stripe. Your booking is confirmed automatically once paid.")}
                  </p>
                </div>
              </div>
            )}

            {p.status === "paid_scheduling_required" && (
              <div className="glass-strong rounded-3xl p-6 md:p-8" data-testid="scheduling-stage">
                <p className="overline text-steel-light mb-4">{t("Choose Your Appointment")}</p>
                <Scheduler token={token} onScheduled={load} t={t} />
              </div>
            )}

            {(p.status === "scheduled" || p.status === "in_progress") && p.appointment && (
              <div className="glass-strong rounded-3xl p-6 md:p-8" data-testid="appointment-card">
                <p className="overline text-steel-light mb-3">{t("Your Appointment")}</p>
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-2xl bg-steel/20 flex items-center justify-center"><CalendarDays className="h-5 w-5 text-steel-light" /></div>
                  <div>
                    <p className="text-white font-600">{fmtDay(p.appointment.date, lang)}</p>
                    <p className="text-white/55 text-sm">{p.appointment.start} {t("arrival window")} · ~{Math.round(p.appointment.duration_min / 60)}h</p>
                  </div>
                </div>
                <p className="mt-4 text-white/40 text-xs">{t("Need to reschedule? Contact us at least 48 hours in advance.")}</p>
              </div>
            )}

            {p.status === "completed" && (
              <div className="glass-strong rounded-3xl p-6 md:p-8 text-center" data-testid="completed-card">
                <Star className="h-8 w-8 text-steel-light mx-auto mb-3" />
                <p className="text-white font-600 mb-2">{t("Thank you for choosing North Peak!")}</p>
                <p className="text-white/55 text-sm">{t("We'd love your feedback — check your email for a quick review request.")}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showAccept && p.pricing && (
          <AcceptDialog p={p} token={token} t={t} onClose={() => setShowAccept(false)}
            onAccepted={() => { setShowAccept(false); load(); }} />
        )}
      </AnimatePresence>
    </div>
  );
}
